import React from "react";
import {
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  LinearProgress,
  Alert,
  Avatar,
  IconButton,
  Stack,
} from "@mui/material";
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  FitnessCenter as FitnessCenterIcon,
  Psychology as PsychologyIcon,
  EmojiObjects as EmojiObjectsIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import NewColors from "../../themes/NewColors";

/**
 * Beautiful photo analysis report component
 * Displays AI-powered fitness analysis with coaching insights
 */
const PhotoAnalysisReport = ({ analysisData, clientName, onClose }) => {
  if (!analysisData || !analysisData.analysis) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="info">
          No analysis data available. Please run an assessment to see results.
        </Alert>
      </Box>
    );
  }

  const { analysis } = analysisData;
  const metadata = analysis.analysis_metadata || {};
  const isComparison = metadata.assessment_type === "comparison_assessment";

  // Helper function to get progress color based on rating
  const getProgressColor = (rating) => {
    if (rating >= 8) return NewColors.status.success;
    if (rating >= 6) return NewColors.triadicPower.accent;
    if (rating >= 4) return NewColors.status.warning;
    return NewColors.status.error;
  };

  const renderProgressIndicator = (rating, maxRating = 10) => {
    const percentage = (rating / maxRating) * 100;
    const color = getProgressColor(rating);

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(percentage, 100)}
          sx={{
            flex: 1,
            height: 8,
            borderRadius: 4,
            backgroundColor: NewColors.border.light,
            "& .MuiLinearProgress-bar": {
              backgroundColor: color,
              borderRadius: 4,
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{ color: NewColors.text.primary, minWidth: 50, fontWeight: 600 }}
        >
          {rating}/10
        </Typography>
      </Box>
    );
  };

  const renderSection = (
    title,
    icon,
    content,
    bgColor = NewColors.cardBackground,
  ) => (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        borderRadius: 2,
        background: bgColor,
        border: `1px solid ${NewColors.border.light}`,
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        {icon}
        <Typography
          variant="h6"
          sx={{ color: NewColors.text.primary, fontWeight: 600 }}
        >
          {title}
        </Typography>
      </Box>
      {content}
    </Paper>
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: NewColors.sectionBackground,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          bgcolor: NewColors.white,
          borderBottom: `1px solid ${NewColors.border.light}`,
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: NewColors.triadicPower.primary,
                width: 56,
                height: 56,
              }}
            >
              <AssessmentIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{ color: NewColors.text.primary, fontWeight: 700 }}
              >
                AI Photo Analysis Report
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mt: 1 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon
                    sx={{ fontSize: 16, color: NewColors.text.secondary }}
                  />
                  <Typography
                    variant="body1"
                    sx={{ color: NewColors.text.secondary, fontWeight: 500 }}
                  >
                    {clientName}
                  </Typography>
                </Box>
                <Chip
                  icon={<ScheduleIcon />}
                  label={new Date(analysis.generated_at).toLocaleDateString()}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={
                    isComparison ? "Progress Analysis" : "Initial Assessment"
                  }
                  size="small"
                  sx={{
                    bgcolor: isComparison
                      ? NewColors.status.success
                      : NewColors.triadicPower.accent,
                    color: NewColors.white,
                    fontWeight: 600,
                  }}
                />
                {metadata.analysis_confidence && (
                  <Chip
                    label={`${metadata.analysis_confidence} confidence`}
                    size="small"
                    sx={{
                      bgcolor: NewColors.triadicPower.primary,
                      color: NewColors.white,
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: NewColors.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        <Grid container spacing={3}>
          {/* Body Composition Analysis */}
          {analysis.body_composition && (
            <Grid item xs={12} md={6}>
              {renderSection(
                "Body Composition",
                <FitnessCenterIcon
                  sx={{ color: NewColors.triadicPower.primary }}
                />,
                <Box>
                  {/* Body Fat Assessment */}
                  {analysis.body_composition.body_fat_assessment && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Body Fat Assessment
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: NewColors.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.body_composition.body_fat_assessment}
                      </Typography>
                    </Box>
                  )}

                  {/* Muscle Definition */}
                  {analysis.body_composition.muscle_definition && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Muscle Definition
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: NewColors.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.body_composition.muscle_definition}
                      </Typography>
                    </Box>
                  )}

                  {/* Physique Development */}
                  {analysis.body_composition.physique_development && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Physique Development
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: NewColors.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.body_composition.physique_development}
                      </Typography>
                    </Box>
                  )}

                  {/* Rating */}
                  {analysis.body_composition.rating && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Overall Rating
                      </Typography>
                      {renderProgressIndicator(
                        analysis.body_composition.rating,
                      )}
                    </Box>
                  )}
                </Box>,
                NewColors.sectionBackgroundTinted.warm,
              )}
            </Grid>
          )}

          {/* Muscle Development Analysis */}
          {analysis.muscle_development && (
            <Grid item xs={12} md={6}>
              {renderSection(
                "Muscle Development",
                <TrendingUpIcon
                  sx={{ color: NewColors.triadicPower.accent }}
                />,
                <Box>
                  {/* Upper Body */}
                  {analysis.muscle_development.upper_body && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Upper Body
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: NewColors.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.muscle_development.upper_body}
                      </Typography>
                    </Box>
                  )}

                  {/* Core */}
                  {analysis.muscle_development.core && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Core
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: NewColors.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.muscle_development.core}
                      </Typography>
                    </Box>
                  )}

                  {/* Lower Body */}
                  {analysis.muscle_development.lower_body && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Lower Body
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: NewColors.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.muscle_development.lower_body}
                      </Typography>
                    </Box>
                  )}

                  {/* Areas of Progress */}
                  {analysis.muscle_development.areas_of_progress &&
                    analysis.muscle_development.areas_of_progress.length >
                      0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: NewColors.text.primary,
                          }}
                        >
                          Areas of Progress
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {analysis.muscle_development.areas_of_progress.map(
                            (area, index) => (
                              <Chip
                                key={index}
                                label={area}
                                size="small"
                                sx={{
                                  bgcolor: NewColors.status.success,
                                  color: NewColors.white,
                                }}
                              />
                            ),
                          )}
                        </Box>
                      </Box>
                    )}

                  {/* Areas Needing Attention */}
                  {analysis.muscle_development.areas_needing_attention &&
                    analysis.muscle_development.areas_needing_attention.length >
                      0 && (
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: NewColors.text.primary,
                          }}
                        >
                          Areas Needing Attention
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {analysis.muscle_development.areas_needing_attention.map(
                            (area, index) => (
                              <Chip
                                key={index}
                                label={area}
                                size="small"
                                sx={{
                                  bgcolor: NewColors.status.warning,
                                  color: NewColors.white,
                                }}
                              />
                            ),
                          )}
                        </Box>
                      </Box>
                    )}
                </Box>,
                NewColors.sectionBackgroundTinted.blue,
              )}
            </Grid>
          )}

          {/* Posture Analysis */}
          {analysis.posture_analysis && (
            <Grid item xs={12} md={6}>
              {renderSection(
                "Posture Analysis",
                <PsychologyIcon
                  sx={{ color: NewColors.triadicPower.primary }}
                />,
                <Box>
                  {/* Alignment Notes */}
                  {analysis.posture_analysis.alignment_notes && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Alignment Notes
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: NewColors.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.posture_analysis.alignment_notes}
                      </Typography>
                    </Box>
                  )}

                  {/* Imbalances */}
                  {analysis.posture_analysis.imbalances_identified &&
                    analysis.posture_analysis.imbalances_identified.length >
                      0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: NewColors.text.primary,
                          }}
                        >
                          Imbalances Identified
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {analysis.posture_analysis.imbalances_identified.map(
                            (imbalance, index) => (
                              <Chip
                                key={index}
                                label={imbalance}
                                size="small"
                                sx={{
                                  bgcolor: `${NewColors.status.warning}20`,
                                  color: NewColors.status.warning,
                                  border: `1px solid ${NewColors.status.warning}40`,
                                }}
                              />
                            ),
                          )}
                        </Box>
                      </Box>
                    )}

                  {/* Improvements Noted */}
                  {analysis.posture_analysis.improvements_noted &&
                    analysis.posture_analysis.improvements_noted.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: NewColors.text.primary,
                          }}
                        >
                          Improvements Noted
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {analysis.posture_analysis.improvements_noted.map(
                            (improvement, index) => (
                              <Chip
                                key={index}
                                label={improvement}
                                size="small"
                                sx={{
                                  bgcolor: NewColors.status.success,
                                  color: NewColors.white,
                                }}
                              />
                            ),
                          )}
                        </Box>
                      </Box>
                    )}

                  {/* Stance Evaluation */}
                  {analysis.posture_analysis.stance_evaluation && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Stance Evaluation
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: NewColors.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.posture_analysis.stance_evaluation}
                      </Typography>
                    </Box>
                  )}
                </Box>,
                NewColors.sectionBackgroundTinted.purple,
              )}
            </Grid>
          )}

          {/* Overall Progress */}
          {analysis.overall_progress && (
            <Grid item xs={12} md={6}>
              {renderSection(
                "Overall Progress",
                <TrendingUpIcon sx={{ color: NewColors.status.success }} />,
                <Box>
                  {/* Progress Rating */}
                  {analysis.overall_progress.progress_rating && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Progress Rating
                      </Typography>
                      {renderProgressIndicator(
                        analysis.overall_progress.progress_rating,
                      )}
                    </Box>
                  )}

                  {/* Chronological Comparison */}
                  {analysis.overall_progress.chronological_comparison && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: NewColors.text.primary,
                        }}
                      >
                        Progress Summary
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: NewColors.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.overall_progress.chronological_comparison}
                      </Typography>
                    </Box>
                  )}

                  {/* Key Improvements */}
                  {analysis.overall_progress.key_improvements &&
                    analysis.overall_progress.key_improvements.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: NewColors.text.primary,
                          }}
                        >
                          Key Improvements
                        </Typography>
                        <Box sx={{ pl: 1 }}>
                          {analysis.overall_progress.key_improvements.map(
                            (improvement, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: NewColors.status.success,
                                    mt: 0.75,
                                    mr: 2,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: NewColors.text.secondary,
                                    lineHeight: 1.6,
                                  }}
                                >
                                  {improvement}
                                </Typography>
                              </Box>
                            ),
                          )}
                        </Box>
                      </Box>
                    )}

                  {/* Trend Direction */}
                  {analysis.overall_progress.trend_direction && (
                    <Box>
                      <Chip
                        icon={<TrendingUpIcon />}
                        label={`Trend: ${analysis.overall_progress.trend_direction}`}
                        sx={{
                          bgcolor:
                            analysis.overall_progress.trend_direction ===
                            "improving"
                              ? NewColors.status.success
                              : NewColors.status.warning,
                          color: NewColors.white,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}
                </Box>,
                NewColors.sectionBackgroundTinted.green,
              )}
            </Grid>
          )}

          {/* Coaching Recommendations */}
          {analysis.coaching_recommendations && (
            <Grid item xs={12}>
              {renderSection(
                "Coaching Recommendations",
                <EmojiObjectsIcon
                  sx={{ color: NewColors.triadicPower.accent }}
                />,
                <Grid container spacing={3}>
                  {Object.entries(analysis.coaching_recommendations).map(
                    ([category, recommendations]) => (
                      <Grid item xs={12} sm={6} md={3} key={category}>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: NewColors.text.primary,
                              fontWeight: 600,
                              mb: 2,
                              textTransform: "capitalize",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: NewColors.triadicPower.accent,
                              }}
                            />
                            {category.replace("_", " ")}
                          </Typography>

                          {Array.isArray(recommendations) ? (
                            <Box>
                              {recommendations.map((rec, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    mb: 1.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 4,
                                      height: 4,
                                      borderRadius: "50%",
                                      bgcolor: NewColors.text.tertiary,
                                      mt: 0.75,
                                      mr: 1.5,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: NewColors.text.secondary,
                                      lineHeight: 1.6,
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {typeof rec === "string"
                                      ? rec
                                      : rec.recommendation || rec}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                color: NewColors.text.secondary,
                                lineHeight: 1.6,
                              }}
                            >
                              {recommendations}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ),
                  )}
                </Grid>,
              )}
            </Grid>
          )}
        </Grid>

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: `1px solid ${NewColors.border.light}`,
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: NewColors.text.tertiary,
              display: "block",
            }}
          >
            Analysis powered by {analysis.model_used} • Vision analysis:{" "}
            {analysis.vision_model}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: NewColors.text.tertiary,
              display: "block",
              mt: 0.5,
            }}
          >
            Generated on {new Date(analysis.generated_at).toLocaleString()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PhotoAnalysisReport;
